import { useState, useEffect } from 'react';
import { Home } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export const MortgageCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(3000000);
  const [interestRate, setInterestRate] = useState(5.5);
  const [loanTerm, setLoanTerm] = useState(25);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    
    if (monthlyRate > 0) {
      const payment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                     (Math.pow(1 + monthlyRate, numPayments) - 1);
      
      setMonthlyPayment(Math.round(payment));
      setTotalPayment(Math.round(payment * numPayments));
      setTotalInterest(Math.round(payment * numPayments - loanAmount));
      
      const data = [];
      let remainingBalance = loanAmount;
      let totalPrincipalPaid = 0;
      let totalInterestPaid = 0;
      
      for (let year = 0; year <= loanTerm; year++) {
        if (year === 0) {
          data.push({ year: 0, jistina: 0, uroky: 0, zustatek: loanAmount });
        } else {
          for (let month = 0; month < 12 && remainingBalance > 0; month++) {
            const interestPayment = remainingBalance * monthlyRate;
            const principalPayment = Math.min(payment - interestPayment, remainingBalance);
            remainingBalance -= principalPayment;
            totalPrincipalPaid += principalPayment;
            totalInterestPaid += interestPayment;
          }
          data.push({
            year: year,
            jistina: Math.round(totalPrincipalPaid),
            uroky: Math.round(totalInterestPaid),
            zustatek: Math.max(0, Math.round(remainingBalance))
          });
        }
      }
      setChartData(data);
    }
  }, [loanAmount, interestRate, loanTerm]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold mb-2">{label}. rok</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value?.toLocaleString('cs-CZ')} Kč
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Měsíční splátka</p>
        <p className="text-4xl font-bold text-green">{monthlyPayment.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Celkem zaplatíte</p>
          <p className="text-white font-bold text-lg">{totalPayment.toLocaleString('cs-CZ')} Kč</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Přeplatíte na úrocích</p>
          <p className="text-gold font-bold text-lg">{totalInterest.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>

      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorJistina" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="jistina" 
              name="Splacená jistina"
              stroke="#22C55E" 
              fill="url(#colorJistina)" 
              strokeWidth={2}
            />
            <Area 
              type="monotone" 
              dataKey="uroky" 
              name="Zaplacené úroky"
              stroke="#EAB308" 
              fill="transparent" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex gap-6 justify-center text-sm">
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-green"></div>
          <span className="text-gray-400">Splacená jistina</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-gold"></div>
          <span className="text-gray-400">Zaplacené úroky</span>
        </div>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="hypoteka"
      title="Hypoteční kalkulačka"
      subtitle="Spočítejte si měsíční splátku hypotéky"
      icon={Home}
      tip="Zvažte mimořádné splátky - i 1000 Kč navíc měsíčně může ušetřit statisíce na úrocích. A nezapomeňte po 3-5 letech prověřit refinancování."
      result={result}
      showResult={true}
      testId="mortgage-calculator"
    >
      <SliderInput
        label="Výše hypotéky"
        value={loanAmount}
        onChange={setLoanAmount}
        min={500000}
        max={15000000}
        step={100000}
        suffix="Kč"
        testId="mortgage-amount-slider"
      />
      <SliderInput
        label="Úroková sazba"
        value={interestRate}
        onChange={setInterestRate}
        min={1}
        max={10}
        step={0.1}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="mortgage-rate-slider"
      />
      <SliderInput
        label="Doba splácení"
        value={loanTerm}
        onChange={setLoanTerm}
        min={5}
        max={35}
        step={1}
        suffix="let"
        testId="mortgage-term-slider"
      />
    </CalculatorCard>
  );
};
